import React, { useEffect, useState } from "react";
import { fetchMyOrders } from "../services/OrderServices";
import { QRCodeCanvas } from "qrcode.react";
import "./OrderPage.css";

const steps = ["Ordered", "Processing", "Shipped", "Delivered"];

export default function MyOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStepper, setOpenStepper] = useState(null);

  const UPI_ID = process.env.REACT_APP_UPI_VPA; // ✅ from .env

  // ✅ Fetch user's orders
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchMyOrders();
        // Always sort newest first
        setOrders((data || []).sort((a, b) => b.id - a.id));
      } catch (err) {
        console.error("fetchMyOrders error", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalToRupees = (o) => {
    if (o.totalAmountRupees) return o.totalAmountRupees;
    if (o.totalAmount) return (Number(o.totalAmount) / 100).toFixed(2);
    return 0;
  };

  const stateToStep = (stateStr) => {
    const s = (stateStr || "").toLowerCase();
    if (s.includes("deliv")) return 3;
    if (s.includes("ship")) return 2;
    if (s.includes("paid") || s.includes("success") || s.includes("completed")) return 1;
    return 0;
  };

  if (loading) return <div className="rn-loading">Loading…</div>;

  if (!orders || orders.length === 0)
    return (
      <div className="rn-empty">
        No orders yet. <a className="link" href="/products">Shop now</a>
      </div>
    );

  return (
    <div className="rn-orders">
      <div className="rn-gradient-bg" />
      <div className="rn-orders-card">
        <div className="rn-header">
          <h2>My Orders</h2>
          <p>Track payments & statuses</p>
        </div>

        <div className="rn-order-list">
          {orders.map((o) => {
            const stepIndex = stateToStep(o.state);
            const rupees = Number(totalToRupees(o)).toFixed(2);
            const orderState = (o.state || "").toUpperCase();
            const isPending = ["CREATED", "PENDING"].includes(orderState);

            // ✅ UPI QR Code payload (for pending)
            const upiQR = `upi://pay?pa=${UPI_ID}&pn=RoupanNaturals&cu=INR&am=${rupees}&tn=Payment%20for%20Order%20${o.merchantOrderId}`;

            return (
              <div className="rn-order" key={o.id}>
                {/* Left Section */}
                <div className="left">
                  <div className="row">
                    <span>Ref</span>
                    <span>{o.merchantOrderId || `#${o.id}`}</span>
                  </div>

                  <div
                    className="row status-row"
                    tabIndex={0}
                    onClick={() =>
                      setOpenStepper(openStepper === o.id ? null : o.id)
                    }
                  >
                    <span>Status</span>
                    <span className={`tag ${orderState.toLowerCase()}`}>
                      {orderState}
                      <span className="stepper-arrow">
                        {openStepper === o.id ? "▲" : "▼"}
                      </span>
                    </span>

                    {/* Stepper dropdown */}
                    <div
                      className={`vertical-stepper-dropdown ${
                        openStepper === o.id ? "open" : ""
                      }`}
                    >
                      <div className="vertical-stepper">
                        {steps.map((s, idx) => (
                          <div
                            key={s}
                            className={`vstep ${
                              idx < stepIndex ? "done" : ""
                            } ${idx === stepIndex ? "active" : ""}`}
                          >
                            <div className="vcircle">
                              {idx < stepIndex ? "✓" : idx + 1}
                            </div>
                            <div className="vlabel">{s}</div>
                            {idx < steps.length - 1 && (
                              <div
                                className={`vbar ${
                                  idx < stepIndex ? "filled" : ""
                                }`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <span>Total</span>
                    <span>₹{rupees}</span>
                  </div>

                  <div className="row">
                    <span>Payment</span>
                    <span>{o.paymentMethod || "—"}</span>
                  </div>

                  <div className="items" style={{ marginTop: 8 }}>
                    {(o.metadata?.cartItems || []).slice(0, 3).map((it, i) => (
                      <span key={i} className="chip">
                        {it.name} × {it.quantity}
                      </span>
                    ))}
                    {o.metadata?.cartItems?.length > 3 && (
                      <span className="chip more">
                        +{o.metadata.cartItems.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Center Section */}
                <div className="center">
                  {["PAID", "SUCCESS", "COMPLETED"].includes(orderState) && (
                    <button
                      className="btn rn-btn rn-btn-outline"
                      style={{ margin: "8px 0" }}
                      onClick={() =>
                        window.open(`/api/orders/${o.id}/invoice.pdf`, "_blank")
                      }
                    >
                      Download Invoice
                    </button>
                  )}
                </div>

                {/* Right Section */}
                <div className="right">
                  {isPending ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "10px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        width: "100%",
                        maxWidth: "160px",
                        margin: "0 auto",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 12,
                          marginBottom: 8,
                          textAlign: "center",
                        }}
                      >
                        Scan to pay via UPI
                      </p>
                      <QRCodeCanvas
                        value={upiQR}
                        size={Math.min(120, window.innerWidth * 0.3)}
                      />
                    </div>
                  ) : (
                    <div className="ok">✓</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
